import { Box, Button, Checkbox, Collapse, Divider, IconButton, InputAdornment, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, MenuList, TextField, Typography } from '@mui/material';
import { ChevronRight, ExpandMore, KeyboardArrowDown } from '@mui/icons-material';
import React from 'react';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import Utils from '@utils/index';
import { extractKeywords, matchesKeywordPeriod } from '@utils/filtering/filter-utils';
import { normalizeMultiCompanySelection } from '@utils/events_inbox/multiCompanySelection';
import SearchHeaderTooltipIcon from '@components/common/SearchHeaderTooltipIcon';

type CompanySelectionState = "all" | "some" | "none";

type CompanySiteSelectorProps = {
  availableCompanies: Company[];
  selectedCompanies: Company[];
  companySites: Record<string, string[]>;
  onSelectionChange: (companies: Company[], companySites: Record<string, string[]>) => void;
  disabled: boolean;
  onListOpen?: () => void;
  onListClose?: () => void;
}

/**
 * Company and site selector used by the Global Event Inbox in multi-company mode.
 * Companies are selected as a whole by default and can be narrowed down to specific sites.
 */
const CompanySiteSelector: React.FC<CompanySiteSelectorProps> = (props) => {
  const { availableCompanies, selectedCompanies, companySites, onSelectionChange, disabled, onListOpen, onListClose } = props;

  const sitesByCompany = useSelector((store: IStoreState) => store.sites.sites);

  const [anchorEl, setAnchorEl] = React.useState<any | null>(null);
  const [filterText, setFilterText] = React.useState<string>("");
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const onListOpenRef = React.useRef(onListOpen);
  const onListCloseRef = React.useRef(onListClose);

  React.useEffect(() => { onListOpenRef.current = onListOpen }, [onListOpen])
  React.useEffect(() => { onListCloseRef.current = onListClose }, [onListClose])
  React.useEffect(() => {
    if (anchorEl !== null && onListOpenRef.current) onListOpenRef.current();
    else if (onListCloseRef.current) onListCloseRef.current();
  }, [anchorEl])

  const selectedCompanyIds = React.useMemo(() => new Set(selectedCompanies.map(el => el.companyId)), [selectedCompanies]);

  const visibleCompanies = React.useMemo(() => {
    const keywords = extractKeywords(filterText);
    const result: Array<{ company: Company, sites: Site[], matchedBySite: boolean }> = [];

    Utils.Array.orderAlphabetically(availableCompanies, 'name').forEach((company: Company) => {
      const sites: Site[] = Utils.Array.orderAlphabetically(sitesByCompany.get(company.companyId) || [], 'name');

      if (keywords.length === 0 || matchesKeywordPeriod(keywords, company, ["name"])) {
        result.push({ company, sites, matchedBySite: false });
        return;
      }

      const matchingSites = sites.filter((site) => matchesKeywordPeriod(keywords, site, ["name"]));
      if (matchingSites.length > 0) result.push({ company, sites: matchingSites, matchedBySite: true });
    })

    return result;
  }, [availableCompanies, sitesByCompany, filterText])

  function getCompanyState(companyId: string): CompanySelectionState {
    if (!selectedCompanyIds.has(companyId)) return "none";
    if (companySites[companyId] !== undefined) return "some";
    return "all";
  }

  function getSelectedSiteIds(companyId: string): Set<string> {
    const state = getCompanyState(companyId);
    if (state === "none") return new Set();
    if (state === "some") return new Set(companySites[companyId]);
    return new Set((sitesByCompany.get(companyId) || []).map(el => el.siteId));
  }

  function emitSelection(companyIds: string[], restrictions: Record<string, string[]>) {
    const normalized = normalizeMultiCompanySelection({ companies: companyIds, companySites: restrictions }, sitesByCompany);
    const normalizedIds = new Set(normalized.companies);
    const companies = availableCompanies
      .filter(el => normalizedIds.has(el.companyId))
      .sort((c1, c2) => (c1?.name || "z").localeCompare((c2?.name || "z")));

    onSelectionChange(companies, normalized.companySites);
  }

  function handleAllCompaniesClick() {
    const everythingSelected = availableCompanies.every(el => getCompanyState(el.companyId) === "all");
    if (everythingSelected) emitSelection([], {});
    else emitSelection(availableCompanies.map(el => el.companyId), {});
  }

  function handleCompanyClick(company: Company) {
    const { companyId } = company;
    const restrictions = { ...companySites };
    delete restrictions[companyId];

    if (getCompanyState(companyId) === "all") {
      emitSelection(Array.from(selectedCompanyIds).filter(el => el !== companyId), restrictions);
    } else {
      emitSelection([...Array.from(selectedCompanyIds), companyId], restrictions);
    }
  }

  function handleSiteClick(company: Company, site: Site) {
    const { companyId } = company;
    const siteIds = getSelectedSiteIds(companyId);

    if (siteIds.has(site.siteId)) siteIds.delete(site.siteId);
    else siteIds.add(site.siteId);

    emitSelection(
      [...Array.from(selectedCompanyIds).filter(el => el !== companyId), companyId],
      { ...companySites, [companyId]: Array.from(siteIds) }
    );
  }

  function toggleExpanded(companyId: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    })
  }

  function getButtonText(): string {
    const partialCount = selectedCompanies.filter(el => companySites[el.companyId] !== undefined).length;

    if (selectedCompanies.length === availableCompanies.length && partialCount === 0) return "All";
    if (partialCount > 0) return `${selectedCompanies.length}/${availableCompanies.length} companies (${partialCount} partial)`;
    return `${selectedCompanies.length}/${availableCompanies.length} companies`;
  }

  function getCompanySubtitle(companyId: string): string {
    const sites = sitesByCompany.get(companyId);
    if (!sites || sites.length === 0) return "Sites unavailable";

    return `${getSelectedSiteIds(companyId).size}/${sites.length} sites`;
  }

  const allCompaniesSelected = availableCompanies.length > 0 && availableCompanies.every(el => getCompanyState(el.companyId) === "all");
  const someCompaniesSelected = !allCompaniesSelected && selectedCompanies.length > 0;

  return (
    <Box width="100%">
      <Button
        disabled={disabled}
        variant="outlined"
        endIcon={<KeyboardArrowDown />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-cy="select-company-site-button"
        color="secondary"
      >
        <Typography style={{ maxWidth: '14rem' }} noWrap={true} variant="button">{getButtonText()}</Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setTimeout(() => { setFilterText("") }, 300)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuList disablePadding>
          <ListSubheader style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }} >
            <TextField
              style={{ marginTop: 0 }}
              autoFocus={true}
              placeholder="Search companies or sites"
              variant="standard"
              defaultValue={filterText}
              onChange={(event) => { event.stopPropagation(); event.preventDefault(); setFilterText(event.target.value); }}
              onKeyDown={(e) => { e.stopPropagation(); }}
              size="medium"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchHeaderTooltipIcon />
                  </InputAdornment>
                ),
              }}
              data-cy="company-site-list-search-input"
            />
          </ListSubheader>
        </MenuList>
        <Divider style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />
        <MenuItem
          style={{ paddingTop: 0, paddingBottom: 0 }}
          dense
          data-cy={`company-site-selector-all-companies-menu-item`}
          onClick={handleAllCompaniesClick}
        >
          <ListItemIcon style={{ minWidth: "4.25rem", justifyContent: 'flex-end' }}>
            <Checkbox
              color="primary"
              size="small"
              checked={allCompaniesSelected}
              indeterminate={someCompaniesSelected}
            />
          </ListItemIcon>
          <ListItemText>
            <Typography noWrap={true} style={{ width: '17rem' }}>All companies</Typography>
          </ListItemText>
        </MenuItem>
        <Divider style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }} />
        <MenuList disablePadding style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {visibleCompanies.length === 0 &&
            <Typography variant="body2" color="textSecondary" style={{ padding: '0.5rem 1rem' }}>No companies or sites found</Typography>
          }
          {visibleCompanies.map(({ company, sites, matchedBySite }) => {
            const state = getCompanyState(company.companyId);
            const hasSites = (sitesByCompany.get(company.companyId) || []).length > 0;
            const isExpanded = hasSites && (matchedBySite || expanded.has(company.companyId));
            const selectedSiteIds = getSelectedSiteIds(company.companyId);

            return (
              <React.Fragment key={company.companyId}>
                <MenuItem
                  dense
                  style={{ paddingTop: 0, paddingBottom: 0 }}
                  data-cy={`company-site-selector-${company?.name}-menu-item`}
                  onClick={() => handleCompanyClick(company)}
                >
                  <ListItemIcon style={{ minWidth: "4.25rem" }}>
                    <IconButton
                      size="small"
                      disabled={!hasSites || matchedBySite}
                      style={{ visibility: hasSites ? 'visible' : 'hidden' }}
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(company.companyId); }}
                      data-cy={`company-site-selector-${company?.name}-expand-button`}
                    >
                      {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                    </IconButton>
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={state === "all"}
                      indeterminate={state === "some"}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography noWrap={true} style={{ width: '17rem' }}>{company.name}</Typography>}
                    secondary={<Typography noWrap={true} variant="caption" color="textSecondary">{getCompanySubtitle(company.companyId)}</Typography>}
                  />
                </MenuItem>
                {hasSites &&
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {sites.map(site => (
                      <MenuItem
                        dense
                        key={site.siteId}
                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: '4.5rem' }}
                        data-cy={`company-site-selector-${company?.name}-${site?.name}-menu-item`}
                        onClick={() => handleSiteClick(company, site)}
                      >
                        <ListItemIcon style={{ minWidth: "2.25rem" }}>
                          <Checkbox
                            color="primary"
                            size="small"
                            checked={selectedSiteIds.has(site.siteId)}
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography noWrap={true} style={{ width: '14rem' }}>{site.name}</Typography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Collapse>
                }
              </React.Fragment>
            )
          })}
        </MenuList>
      </Menu>
    </Box>
  )
}

export default CompanySiteSelector;
